/* ==========================================================================
   Eneruji — Home page bundle
   Ported from the standalone landing page into a Shopify section.
   Depends on GSAP + ScrollTrigger + Lenis being loaded first (non-module).
   All DOM ids are prefixed "eh-" and selectors scoped to .eneruji-home.
   ========================================================================== */

/* -------------------------------------------------------------------------
   1) Core: preloader, smooth scroll, nav, mobile menu, magnetic UI
   ------------------------------------------------------------------------- */
(function () {
  "use strict";
  if (!document.querySelector(".eneruji-home")) return;

  var hasGSAP = typeof window.gsap !== "undefined";
  if (hasGSAP && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  function initPreloader() {
    var pre = document.getElementById("eh-preloader");
    var fill = document.getElementById("eh-preloader-fill");
    var count = document.getElementById("eh-preloader-count");
    if (!pre) return Promise.resolve();

    return new Promise(function (resolve) {
      var progress = { v: 0 };
      var target = 0;
      var imgs = Array.prototype.slice.call(document.images);
      var total = imgs.length || 1;
      var loaded = 0;

      function bump() {
        loaded++;
        target = Math.min(100, Math.round((loaded / total) * 100));
      }
      imgs.forEach(function (img) {
        if (img.complete) bump();
        else {
          img.addEventListener("load", bump);
          img.addEventListener("error", bump);
        }
      });

      var finished = false;
      var minTimer = setTimeout(function () { target = Math.max(target, 40); }, 200);

      var raf = function () {
        progress.v += (target - progress.v) * 0.12;
        if (target >= 100 && progress.v > 99.3) progress.v = 100;
        if (fill) fill.style.width = progress.v + "%";
        if (count) count.textContent = Math.round(progress.v);

        if (progress.v >= 100 && !finished) {
          finished = true;
          clearTimeout(minTimer);
          setTimeout(function () {
            pre.classList.add("is-hidden");
            document.body.classList.remove("eneruji-home-lock");
            document.documentElement.classList.remove("eneruji-home-lock");
            setTimeout(function () {
              pre.style.display = "none";
              resolve();
            }, 750);
          }, 220);
          return;
        }
        requestAnimationFrame(raf);
      };

      document.body.classList.add("eneruji-home-lock");
      requestAnimationFrame(raf);
      setTimeout(function () { target = 100; }, 3200);
    });
  }

  // Desktop scroll is intentionally native (no Lenis/scroll-hijacking) so it
  // behaves like standard, seamless browser scrolling - same feel as mobile.
  function scrollToTarget(target) {
    var el = document.querySelector(target);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth" });
  }
  window.__enerujiScrollTo = scrollToTarget;

  function initNav() {
    var nav = document.getElementById("eh-nav");
    if (!nav) return;
    var lastY = window.scrollY;

    window.addEventListener("scroll", function () {
      var y = window.scrollY;
      nav.classList.toggle("is-scrolled", y > 40);
      if (y > lastY && y > 200 && !document.body.classList.contains("eneruji-menu-open")) {
        nav.classList.add("is-hidden");
      } else {
        nav.classList.remove("is-hidden");
      }
      lastY = y;
    }, { passive: true });

    var sections = document.querySelectorAll(".eneruji-home [data-theme]");
    if (hasGSAP && window.ScrollTrigger) {
      sections.forEach(function (sec) {
        var theme = sec.getAttribute("data-theme");
        ScrollTrigger.create({
          trigger: sec,
          start: "top 90px",
          end: "bottom 90px",
          onEnter: function () { nav.classList.toggle("is-light", theme === "light"); },
          onEnterBack: function () { nav.classList.toggle("is-light", theme === "light"); }
        });
      });
    }
  }

  function initMenu() {
    var btn = document.getElementById("eh-burger");
    var overlay = document.getElementById("eh-menu");
    if (!btn || !overlay) return;
    var links = overlay.querySelectorAll(".menu-overlay__links a");
    var open = false;

    function setOpen(v) {
      open = v;
      document.body.classList.toggle("eneruji-menu-open", open);
      btn.setAttribute("aria-expanded", String(open));
      btn.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      if (hasGSAP) {
        if (open) {
          gsap.to(links, { opacity: 1, y: 0, duration: .7, stagger: .06, ease: "power3.out", delay: .25 });
        } else {
          gsap.set(links, { opacity: 0, y: 24 });
        }
      }
    }

    btn.addEventListener("click", function () { setOpen(!open); });
    links.forEach(function (a) {
      a.addEventListener("click", function (e) {
        var href = a.getAttribute("href");
        setOpen(false);
        if (href && href.charAt(0) === "#") {
          e.preventDefault();
          setTimeout(function () { scrollToTarget(href); }, 300);
        }
        // Real URLs (Shop, Contact) are left to navigate normally.
      });
    });
  }

  function initAnchorLinks() {
    document.querySelectorAll('.eneruji-home a[data-nav-link]').forEach(function (a) {
      if (a.closest(".menu-overlay")) return;
      a.addEventListener("click", function (e) {
        var href = a.getAttribute("href");
        if (href && href.charAt(0) === "#") {
          e.preventDefault();
          scrollToTarget(href);
        }
      });
    });
    var top = document.getElementById("eh-back-to-top");
    if (top) top.addEventListener("click", function () { scrollToTarget("#eh-top"); });
  }

  function initMagnetic() {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    document.querySelectorAll(".eneruji-home [data-magnetic]").forEach(function (el) {
      var strength = 0.35;
      el.addEventListener("mousemove", function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        if (hasGSAP) gsap.to(el, { x: x, y: y, duration: .4, ease: "power3.out" });
        else el.style.transform = "translate(" + x + "px," + y + "px)";
      });
      el.addEventListener("mouseleave", function () {
        if (hasGSAP) gsap.to(el, { x: 0, y: 0, duration: .5, ease: "elastic.out(1, .4)" });
        else el.style.transform = "";
      });
    });
  }

  function boot() {
    initNav();
    initMenu();
    initAnchorLinks();
    initMagnetic();
    initPreloader().then(function () {
      document.dispatchEvent(new CustomEvent("eneruji:ready"));
      if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();


/* -------------------------------------------------------------------------
   2) Hero canvas: drifting particles
   ------------------------------------------------------------------------- */
(function () {
  "use strict";
  var canvas = document.getElementById("eh-hero-canvas");
  if (!canvas) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var ctx = canvas.getContext("2d");
  var particles = [];
  var w, h, dpr;
  var running = false;
  var rafId = null;
  var COLORS = ["239,193,43", "140,187,60", "238,123,34", "246,243,236"];

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    buildParticles();
  }
  function buildParticles() {
    var count = Math.round((w * h) / 22000);
    count = Math.max(30, Math.min(count, 90));
    particles = [];
    for (var i = 0; i < count; i++) particles.push(makeParticle(true));
  }
  function makeParticle(randomY) {
    var r = Math.random();
    return {
      x: Math.random() * w,
      y: randomY ? Math.random() * h : h + 20,
      radius: 0.6 + Math.random() * 2.1,
      speed: 0.15 + Math.random() * 0.5,
      drift: (Math.random() - 0.5) * 0.4,
      alpha: 0.15 + Math.random() * 0.35,
      color: COLORS[Math.floor(r * COLORS.length)],
      sway: Math.random() * Math.PI * 2
    };
  }
  function step() {
    ctx.clearRect(0, 0, w, h);
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.y -= p.speed;
      p.sway += 0.01;
      p.x += Math.sin(p.sway) * 0.25 + p.drift * 0.05;
      if (p.y < -10) { particles[i] = makeParticle(false); continue; }
      ctx.beginPath();
      ctx.fillStyle = "rgba(" + p.color + "," + p.alpha + ")";
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    rafId = requestAnimationFrame(step);
  }
  function start() { if (running) return; running = true; rafId = requestAnimationFrame(step); }
  function stop() { running = false; if (rafId) cancelAnimationFrame(rafId); }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.isIntersecting ? start() : stop(); });
  }, { threshold: 0 });

  window.addEventListener("resize", resize);
  document.addEventListener("eneruji:ready", function () { resize(); io.observe(canvas); });
})();


/* -------------------------------------------------------------------------
   3) Scroll-driven reveals, hero intro, manifesto scrub, parallax
   ------------------------------------------------------------------------- */
(function () {
  "use strict";
  if (typeof window.gsap === "undefined") return;
  if (!document.querySelector(".eneruji-home")) return;
  var hasST = !!window.ScrollTrigger;
  if (hasST) gsap.registerPlugin(ScrollTrigger);
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function splitManifesto() {
    var el = document.getElementById("eh-manifesto-text");
    if (!el) return;
    var text = el.textContent.trim();
    var words = text.split(/\s+/);
    el.innerHTML = words.map(function (w) { return '<span class="word">' + w + "</span>"; }).join(" ");
  }
  function heroIntro() {
    var tl = gsap.timeline({ defaults: { ease: "power4.out" } });
    tl.to(".eneruji-home .hero__eyebrow span", { y: "0%", duration: 1 }, 0.05)
      .to(".eneruji-home .hero__line span", { y: "0%", duration: 1.1, stagger: 0.11 }, 0.18)
      .to(".eneruji-home .hero__pack", { opacity: 1, y: 0, scale: 1, duration: 1.4, ease: "power3.out" }, 0.55)
      .to(".eneruji-home .hero__sub span", { y: "0%", duration: 1 }, 0.95);
  }
  function heroParallax() {
    var hero = document.getElementById("eh-hero");
    var pack = document.getElementById("eh-hero-pack");
    if (!hero || !pack || window.matchMedia("(pointer: coarse)").matches || reduced) return;
    var xTo = gsap.quickTo(pack, "x", { duration: 0.9, ease: "power3.out" });
    var yTo = gsap.quickTo(pack, "y", { duration: 0.9, ease: "power3.out" });
    hero.addEventListener("mousemove", function (e) {
      var r = hero.getBoundingClientRect();
      var relX = (e.clientX - r.left) / r.width - 0.5;
      var relY = (e.clientY - r.top) / r.height - 0.5;
      xTo(relX * 34);
      yTo(relY * 24);
    });
    hero.addEventListener("mouseleave", function () { xTo(0); yTo(0); });
    if (hasST) {
      gsap.to(pack, { y: "16%", ease: "none", scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true } });
    }
  }
  function manifestoScrub() {
    var section = document.querySelector(".eneruji-home .manifesto");
    var words = document.querySelectorAll(".eneruji-home #eh-manifesto-text .word");
    if (!section || !words.length) return;
    if (!hasST) { words.forEach(function (w) { w.classList.add("word--lit"); }); return; }
    gsap.timeline({ scrollTrigger: { trigger: section, start: "top 78%", end: "bottom 60%", scrub: 0.4 } })
      .to(words, { opacity: 1, stagger: 0.04, ease: "none" });
  }
  function genericReveals() {
    var els = document.querySelectorAll(".eneruji-home [data-reveal]");
    if (!els.length) return;
    if (!hasST) { els.forEach(function (el) { el.classList.add("reveal-in"); }); return; }
    ScrollTrigger.batch(".eneruji-home [data-reveal]", {
      start: "top 88%",
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, { opacity: 1, y: 0, duration: 1, stagger: 0.1, ease: "power3.out" });
      }
    });
  }
  function ctaTitleReveal() {
    if (!hasST) return;
    ScrollTrigger.create({
      trigger: ".eneruji-home .cta",
      start: "top 75%",
      once: true,
      onEnter: function () { gsap.to(".eneruji-home .cta__title span", { y: "0%", duration: 1.1, ease: "power4.out" }); }
    });
  }
  function storyTilt() {
    var media = document.querySelector(".eneruji-home .story__media");
    if (!media || window.matchMedia("(pointer: coarse)").matches || reduced) return;
    var rx = gsap.quickTo(media, "rotateX", { duration: 0.6, ease: "power3.out" });
    var ry = gsap.quickTo(media, "rotateY", { duration: 0.6, ease: "power3.out" });
    media.style.transformPerspective = 900;
    media.addEventListener("mousemove", function (e) {
      var r = media.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      rx(py * -8);
      ry(px * 8);
    });
    media.addEventListener("mouseleave", function () { rx(0); ry(0); });
  }

  document.addEventListener("eneruji:ready", function () {
    splitManifesto();
    heroIntro();
    heroParallax();
    manifestoScrub();
    genericReveals();
    ctaTitleReveal();
    storyTilt();
    setTimeout(function () { if (hasST) ScrollTrigger.refresh(); }, 300);
  });
  window.addEventListener("load", function () { if (hasST) ScrollTrigger.refresh(); });
})();


/* -------------------------------------------------------------------------
   4) Interactive mixer
   ------------------------------------------------------------------------- */
(function () {
  "use strict";
  var waterRange = document.getElementById("eh-water-range");
  var powderRange = document.getElementById("eh-powder-range");
  if (!waterRange || !powderRange) return;

  var waterVal = document.getElementById("eh-water-val");
  var powderVal = document.getElementById("eh-powder-val");
  var powderUnit = document.getElementById("eh-powder-unit");
  var statConsistency = document.getElementById("eh-stat-consistency");
  var statPowder = document.getElementById("eh-stat-powder");
  var liquidFill = document.getElementById("eh-liquid-fill");
  var liquidWave = document.getElementById("eh-liquid-wave");
  var stopTop = document.getElementById("eh-liquid-stop-top");
  var stopBottom = document.getElementById("eh-liquid-stop-bottom");
  var flavorDots = document.querySelectorAll(".eneruji-home .flavor-dot");
  var flaskVisual = document.querySelector(".eneruji-home .mixer__visual");

  var hasGSAP = typeof window.gsap !== "undefined";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var FLAVORS = {
    lemon:  { base: "#EFC12B", deep: "#C79A12" },
    apple:  { base: "#8CBB3C", deep: "#5C8A20" },
    orange: { base: "#EE7B22", deep: "#C6570F" }
  };
  var GRAMS_PER_SCOOP = 47;
  var SCOOP_LABELS = { "0.5": "½", "1": "1", "2": "2" };
  var DOSAGES = (powderRange.dataset.dosages || "0.5,1,2").split(",").map(Number);
  var WATER_MIN = parseFloat(waterRange.min) || 0;
  var WATER_MAX = parseFloat(waterRange.max) || 150;

  var state = { flavor: "lemon", water: parseFloat(waterRange.value) || WATER_MAX, powder: DOSAGES[parseInt(powderRange.value, 10)] || 1 };

  function hexToRgb(hex) {
    var m = hex.replace("#", "");
    var num = parseInt(m, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function lerpColor(hexA, hexB, t) {
    var a = hexToRgb(hexA), b = hexToRgb(hexB);
    return "rgb(" + Math.round(lerp(a[0], b[0], t)) + "," + Math.round(lerp(a[1], b[1], t)) + "," + Math.round(lerp(a[2], b[2], t)) + ")";
  }
  function mixWithWhite(hex, amt) { return lerpColor("#ffffff", hex, 1 - amt); }

  var BASELINE_Y = 200;
  var FULL_Y = 150;
  var EMPTY_Y = 355;

  var water = { current: 0, target: 0 };
  var slosh = { angle: 0, angleVel: 0, velocity: 0 };
  var loopStarted = false;

  function tick(time) {
    water.current += (water.target - water.current) * 0.14;
    var maxTilt = reduced ? 5 : 11;
    var maxAngle = reduced ? 6 : 15;
    slosh.velocity *= 0.91;
    var targetAngle = Math.max(-maxTilt, Math.min(maxTilt, slosh.velocity * (reduced ? 0.2 : 0.4)));
    var force = (targetAngle - slosh.angle) * 0.12;
    slosh.angleVel = (slosh.angleVel + force) * 0.8;
    slosh.angle += slosh.angleVel;
    slosh.angle = Math.max(-maxAngle, Math.min(maxAngle, slosh.angle));

    if (liquidFill) liquidFill.style.transform = "translateY(" + water.current.toFixed(2) + "px)";
    if (liquidWave) {
      var idleAmp = reduced ? 1.6 : 3.2;
      var idleSway = Math.sin(time * 0.0035) * idleAmp;
      var skew = slosh.angle + idleSway;
      var kick = reduced ? 0 : Math.min(9, Math.abs(slosh.velocity) * 0.22);
      var bob = Math.sin(time * 0.003) * (reduced ? 2 : 4) + Math.sin(time * 0.0062 + 1.3) * kick;
      liquidWave.style.transform = "translateY(" + (water.current + bob).toFixed(2) + "px) skewX(" + skew.toFixed(2) + "deg)";
    }
    requestAnimationFrame(tick);
  }
  function startLoop() { if (loopStarted) return; loopStarted = true; requestAnimationFrame(tick); }

  function render() {
    var f = FLAVORS[state.flavor];
    var t = (state.powder - 0.5) / 1.5;
    var topColor = lerpColor(mixWithWhite(f.base, 0.4), f.base, t);
    var bottomColor = lerpColor(f.base, f.deep, t);
    if (stopTop) stopTop.setAttribute("stop-color", topColor);
    if (stopBottom) stopBottom.setAttribute("stop-color", bottomColor);

    var waterPct = (state.water - WATER_MIN) / (WATER_MAX - WATER_MIN);
    water.target = lerp(EMPTY_Y, FULL_Y, waterPct) - BASELINE_Y;
    startLoop();

    var ratioIndex = (state.powder * 100) / state.water;
    var label = "Balanced";
    if (ratioIndex < 0.6) label = "Light";
    else if (ratioIndex > 1.8) label = "Intense";
    if (statConsistency) statConsistency.textContent = label;
    if (statPowder) statPowder.textContent = state.powder * GRAMS_PER_SCOOP;
    if (powderVal) powderVal.textContent = SCOOP_LABELS[String(state.powder)] || state.powder;
    if (powderUnit) powderUnit.textContent = state.powder <= 1 ? "scoop" : "scoops";
    if (waterVal) waterVal.textContent = state.water;
  }

  function splashImpulse(strength) {
    slosh.velocity += strength;
    if (!hasGSAP || reduced || !flaskVisual) return;
    gsap.killTweensOf(flaskVisual);
    gsap.timeline()
      .to(flaskVisual, { rotation: strength, duration: 0.14, ease: "power2.out" })
      .to(flaskVisual, { rotation: -strength * 0.5, duration: 0.22, ease: "power2.inOut" })
      .to(flaskVisual, { rotation: 0, duration: 0.4, ease: "elastic.out(1, 0.45)" });
  }

  waterRange.addEventListener("input", function () {
    var next = parseInt(waterRange.value, 10);
    slosh.velocity += (next - state.water) * 0.5;
    state.water = next;
    render();
  });
  powderRange.addEventListener("input", function () {
    var next = DOSAGES[parseInt(powderRange.value, 10)];
    slosh.velocity += (next - state.powder) * 20;
    state.powder = next;
    render();
  });
  flavorDots.forEach(function (dot) {
    dot.addEventListener("click", function () {
      flavorDots.forEach(function (d) { d.classList.remove("is-active"); d.setAttribute("aria-checked", "false"); });
      dot.classList.add("is-active");
      dot.setAttribute("aria-checked", "true");
      state.flavor = dot.getAttribute("data-flavor");
      render();
      splashImpulse(9);
    });
  });

  render();

  var scrollBound = false;
  function bindScrollVelocity() {
    if (scrollBound) return;
    scrollBound = true;
    if (window.__lenis && window.__lenis.on) {
      window.__lenis.on("scroll", function (e) {
        if (e && typeof e.velocity === "number") slosh.velocity += e.velocity * 14;
      });
    } else {
      var lastY = window.scrollY;
      window.addEventListener("scroll", function () {
        var y = window.scrollY;
        slosh.velocity += (y - lastY);
        lastY = y;
      }, { passive: true });
    }
    startLoop();
  }
  document.addEventListener("eneruji:ready", bindScrollVelocity);
  setTimeout(function () { if (!scrollBound) bindScrollVelocity(); }, 1500);
})();


/* -------------------------------------------------------------------------
   5) CTA newsletter form
   ------------------------------------------------------------------------- */
(function () {
  "use strict";
  var form = document.getElementById("eh-cta-form");
  if (!form) return;
  var input = document.getElementById("eh-cta-email");
  var msg = document.getElementById("eh-cta-form-msg");
  var CONFIG = { endpoint: null };

  function isValidEmail(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value); }
  function showMessage(text) { msg.textContent = text; msg.classList.add("is-visible"); }

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    var value = input.value.trim();
    if (!isValidEmail(value)) {
      form.classList.add("is-invalid");
      showMessage("That email doesn't look right - try again.");
      input.focus();
      return;
    }
    form.classList.remove("is-invalid");
    var btn = form.querySelector("button");
    btn.disabled = true;
    var send = CONFIG.endpoint
      ? fetch(CONFIG.endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: value }) })
      : Promise.resolve();
    send.then(function () {
      showMessage("You're on the list - first batch news lands in your inbox.");
      form.reset();
    }).catch(function () {
      showMessage("Something went wrong. Please try again in a moment.");
    }).finally(function () {
      btn.disabled = false;
    });
  });

  input.addEventListener("input", function () { form.classList.remove("is-invalid"); });
})();
